import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight } from 'lucide-react';

/**
 * MobileStickyCTA — thin always-visible action bar at the bottom of the
 * viewport on mobile. Adapts copy based on auth state.
 *
 * Why this exists: on mobile the hero CTAs scroll out of view after the
 * first swipe. Every premium parenting brand keeps a persistent CTA in
 * the user's thumb-zone — it's a measurable conversion win, not pure
 * aesthetic.
 *
 * Design rules:
 *   - sm:hidden — desktop has Navbar CTAs always visible, this would
 *     be redundant and visually busy
 *   - Slides in after the user scrolls past the hero (~280px) so it
 *     doesn't compete with the hero's own CTAs on first paint
 *   - Cream-50 background + top hairline; the bar reads as a layer over
 *     the page, not part of it
 *   - One primary action only. Two CTAs would split intent and clutter
 *     the bar; users who want to find someone else's registry have nav
 *     access to that.
 */
export const MobileStickyCTA = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 280);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Adapt label based on auth state. Signed-in users with no registry
  // still see "Begin" — they may have signed up months ago and not
  // started yet. The action target handles the actual gating.
  const label = user ? 'Open your registry' : 'Begin your registry';
  const target = user ? '/dashboard' : '/registry/new';

  return (
    <>
      {/* Spacer — reserves layout room at the bottom of the page when the
          bar is visible, so the last content row isn't covered by it. */}
      <div
        aria-hidden
        className={`sm:hidden ${visible ? 'h-[76px]' : 'h-0'} transition-[height] duration-200`}
      />

      <div
        aria-hidden={!visible}
        className={`sm:hidden fixed inset-x-0 bottom-0 z-40 bg-cream-50/95 backdrop-blur-md
          border-t border-border/80 shadow-[0_-8px_24px_rgba(60,36,22,0.06)]
          transition-transform duration-300 ease-out
          ${visible ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="container-page py-3">
          <button
            onClick={() => navigate(target)}
            className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-clay-600 hover:bg-clay-700 text-cream-50 font-medium text-[15px] shadow-soft-1 transition-colors"
            data-testid="mobile-sticky-cta"
          >
            {label}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileStickyCTA;
